import { TrackEvent } from '@bnnvara/analytics';
import { Close } from '@bnnvara/icons/src/components/UserInterfaceIcons/Close';
import BaseNotification from '@bnnvara/notifications';
import { differenceInSeconds, isSameDay } from 'date-fns';
import PropTypes from 'prop-types';
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable dot-notation */
import React, { memo, useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';

import * as S from './styled';

const Notification = memo(
  ({
    visible,
    title,
    children,
    type,
    closeText,
    horizontalPosition,
    direction,
    delay,
    onClose,
    hideAfterSecs,
    OnlyShowOnceEvery24Hrs,
    showCloseButton,
  }) => {
    const [isNotificationDisabled, setIsNotificationDisabled] = useState(false);
    const [ready, setReady] = useState(false);
    const [cookies, setCookie] = useCookies(['cookie-name']);
    const cookieValue = cookies[type] ? cookies[type] : false;
    let isFirstSession = false;

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const setValueCookieWithNewDate = (cookieName) => {
      setCookie(cookieName, new Date());
    };

    const isSameDayFn = (valueCookie) => isSameDay(valueCookie, new Date());

    // we check how long ago the user accepted the cookies for the first time ( === started their first session)
    const secondsSinceCookieConsent = differenceInSeconds(
      cookies['Cookie_Consent'],
      new Date()
    );

    useEffect(() => {
      // if the user accepted the cookies less than 10 seconds ago, we decide it's their first session.
      isFirstSession = secondsSinceCookieConsent > -10;

      if (!cookieValue) {
        setValueCookieWithNewDate(type);
      }

      // if OnlyShowOnceEvery24hrs is true, do not show notification if usercookie of type 'BrandFollowNotification' has the same date as today, except for users that visit the site for the first time.
      if (
        cookieValue &&
        OnlyShowOnceEvery24Hrs &&
        isSameDayFn(cookieValue) &&
        !isFirstSession
      ) {
        setIsNotificationDisabled(true);
      } else {
        // update brandFollowNotification cookie with the current date so our next 'isSameDay'-check will work out.
        setValueCookieWithNewDate(type);
      }
    }, [
      OnlyShowOnceEvery24Hrs,
      cookies,
      setCookie,
      isNotificationDisabled,
      setValueCookieWithNewDate,
      type,
      cookieValue,
      secondsSinceCookieConsent,
    ]);

    // eslint-disable-next-line consistent-return
    useEffect(() => {
      if (delay !== 0) {
        const timer = setTimeout(() => {
          setReady(true);
        }, delay);
        return () => clearTimeout(timer);
      }
      setReady(true);
    }, [delay]);

    // eslint-disable-next-line consistent-return
    useEffect(() => {
      let timer;
      if (hideAfterSecs) {
        timer = setTimeout(() => {
          setIsNotificationDisabled(true);
        }, hideAfterSecs + delay);
        return () => clearTimeout(timer);
      }
    }, [hideAfterSecs, isNotificationDisabled]);

    const disableNotification = () => {
      setIsNotificationDisabled(true);
      if (typeof onClose === 'function') {
        onClose();
        TrackEvent(`Notification-${type}`, 'click', 'sluiten');
      }
    };

    return (
      <React.Fragment>
        {ready && (
          <BaseNotification
            visible={visible}
            onClose={disableNotification}
            disabled={isNotificationDisabled}
            horizontalPosition={horizontalPosition}
            direction={direction}
          >
            <S.StyledTitle>{title}</S.StyledTitle>
            {children}
            {closeText && (
              <S.NotificationCloseText onClick={disableNotification}>
                {closeText}
              </S.NotificationCloseText>
            )}
            {showCloseButton && (
              <S.CloseButton
                onClick={disableNotification}
                disabled={isNotificationDisabled}
              >
                <Close color="black" size={18} />
              </S.CloseButton>
            )}
          </BaseNotification>
        )}
      </React.Fragment>
    );
  }
);

Notification.defaultProps = {
  horizontalPosition: 'center',
  direction: 'down',
  delay: 0,
  onClose: null,
  children: null,
  closeText: null,
  hideAfterSecs: null,
  OnlyShowOnceEvery24Hrs: false,
  showCloseButton: true,
};

Notification.propTypes = {
  title: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  visible: PropTypes.bool.isRequired,
  children: PropTypes.node,
  delay: PropTypes.number,
  closeText: PropTypes.string,
  horizontalPosition: PropTypes.string,
  direction: PropTypes.string,
  onClose: PropTypes.func,
  hideAfterSecs: PropTypes.number,
  OnlyShowOnceEvery24Hrs: PropTypes.bool,
  showCloseButton: PropTypes.bool,
};

export default Notification;
